import { Dispatch } from 'react'
import LoggerService from '../../../services/logger.service'
import { getSha256 } from '../../../utilities'
import { CrewService } from '../../crew/services/crew.service'
import { ICrewListMember } from '../../pcr/models/crewList.V1'
import { AuthenticationService } from '../services/authentication.service'
import LocalStorageService from '../../../services/localStorage.service'
import { IServiceList } from '../../serviceSelection/store/serviceSelection.types'
import {
  setAuthorization,
  setEmail,
  setDatabaseUserName,
  setService,
  setUserId,
  setUserName,
} from '../../user/store/user.actions'
import * as serviceActions from '../../serviceSelection/store/serviceSelection.actions'
import { identifyUserForLogRocket } from '../../zLogRocket/identify'
import { EndOfSupportService } from '../services/endOfSupport.service'
import { setErrorMessage, showToast } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { ZollAPIService } from '../../../services/zollApi.service'
import { setAuthenticationJwt } from '../store/login.actions'

export const hashPin = async (pin: string, serviceId: string, userId: string): Promise<string> => {
  const strippedPin = pin.replace(/-/g, '')

  const hashInput = `${strippedPin}_${serviceId}_${userId}`
  const hashedPin = await getSha256(hashInput)

  return hashedPin.toString()
}

export const verifyPin = async (
  enteredPin: string,
  actualPin: string | null,
  userId: string,
  serviceId: string
): Promise<boolean> => {
  const inputPin = await hashPin(enteredPin, serviceId, userId)

  if (!actualPin) {
    LoggerService.warn(`No PIN found for user ${userId} on service ${serviceId}`)
    return false
  }

  if (inputPin.toUpperCase() === actualPin.toUpperCase()) {
    LoggerService.info(`PIN verified for user ${userId} on service ${serviceId}`)
    return true
  }

  LoggerService.warn(`PIN verification failed for user ${userId} on service ${serviceId}`)
  return false
}

export const verifyUserPin = async (
  pin: string,
  userId: string,
  serviceId: string
): Promise<{ success: boolean; error?: string }> => {
  if (!pin || pin.trim() === '') {
    return { success: false, error: 'PIN is required' }
  }

  const crewService = new CrewService(serviceId)
  const serviceUsers = await crewService.getCrewList()

  if (!serviceUsers) {
    return { success: false, error: 'Failed to fetch user data' }
  }

  const entity = serviceUsers.entity as Array<ICrewListMember>
  const userObj = entity.find((user) => Number(user.userId) === Number(userId))

  if (!userObj?.pin) {
    return { success: false, error: 'No PIN found for user' }
  }

  const isValid = await verifyPin(pin, userObj.pin, userId, serviceId)

  if (!isValid) {
    return { success: false, error: 'Incorrect PIN, please try again' }
  }

  return { success: true }
}

export const getOfflineCredentials = (username: string) => {
  const authState = AuthenticationService.getOfflineAuthState(username)

  if (!authState?.accessToken) {
    LoggerService.warn(`No offline auth state found for user: ${username}`)
    return null
  }

  const { claims } = authState.accessToken
  const databaseUserName = claims.sub as string
  const nameId = claims.nameId as string

  return { authState, databaseUserName, nameId }
}

export const loadStoredServiceContext = (nameId: string, databaseUserName: string) => {
  const serviceListObj = LocalStorageService.getItemForUserIdElseDatabaseUserName(
    nameId,
    databaseUserName,
    'serviceList'
  )

  if (!serviceListObj) {
    LoggerService.warn(`No stored service list found for user: ${databaseUserName}`)
    return null
  }

  let serviceList: IServiceList
  try {
    serviceList = JSON.parse(serviceListObj)
  } catch {
    LoggerService.error(`Corrupted service list in storage for user: ${databaseUserName}`)
    return null
  }

  if (!serviceList?.length) {
    LoggerService.warn(`Empty service list for user: ${databaseUserName}`)
    return null
  }

  const userId = serviceList[0].userId
  let serviceId = localStorage.getItem(`${userId}:serviceId`)
  let serviceName = localStorage.getItem(`${userId}:serviceName`)

  if (serviceList.length === 1) {
    serviceId = serviceList[0].id.toString()
    serviceName = serviceList[0].name
  } else if (!serviceId) {
    LoggerService.warn(
      `User ${userId} has multiple services but no previously selected serviceId in storage. PIN hash may be incorrect.`
    )
  }

  return { serviceList, userId, serviceId, serviceName }
}

export const readStoredEmail = (nameId: string, databaseUserName: string): string | null => {
  return LocalStorageService.getItemForUserIdElseDatabaseUserName(nameId, databaseUserName, 'email')
}

export const restoreSessionToStore = (
  dispatch: Dispatch<any>,
  params: {
    username: string
    userId: number
    email: string | null
    serviceId: string | null
    serviceName: string | null
    serviceList: IServiceList
    authState: any
    databaseUserName: string
  }
): void => {
  dispatch(setUserName(params.username))
  dispatch(setUserId(params.userId))
  dispatch(setEmail(params.email))
  dispatch(setAuthorization(params.authState))
  dispatch(setAuthenticationJwt(params.authState?.accessToken?.accessToken))
  dispatch(setDatabaseUserName(params.databaseUserName))
  dispatch(serviceActions.setServicesList(params.serviceList))
  dispatch(serviceActions.setIsLoading(false))
  dispatch(setService(params.serviceId, params.serviceName))
}

export const completeSessionRestore = (
  dispatch: Dispatch<any>,
  params: {
    username: string
    nameId: string
    databaseUserName: string
    history: any
    successRoute: string
  }
): void => {
  const userIdForLogRocket = params.nameId ? params.nameId.toString() : params.username
  identifyUserForLogRocket(userIdForLogRocket, params.username)

  LoggerService.log(
    `Resume Session Login success for user: ${params.username} (${userIdForLogRocket}) - dbUsername: ${params.databaseUserName}`
  )

  const eosMessageForVersion = EndOfSupportService.getEosMessageForVersion()
  if (eosMessageForVersion) {
    dispatch(setErrorMessage(eosMessageForVersion))
    dispatch(showToast(true))
  }

  params.history.push(params.successRoute)
}

export const handleOfflineLogin = async (
  username: string,
  dispatch: Dispatch<any>,
  setIsLoading: (loading: boolean) => void,
  onFailure: (response: any) => void,
  t: any,
  history: any,
  successRoute: string
): Promise<void> => {
  const usernameLower = username.toLowerCase()

  try {
    const tokenClaims = getOfflineCredentials(usernameLower)
    if (!tokenClaims) {
      AuthenticationService.setOfflineAuthState(usernameLower, false)
      onFailure(ZollAPIService.createMockErrorResponse(401, t('login.loginForm.defaultAuthError')))
      return
    }

    const { databaseUserName, nameId } = tokenClaims

    const serviceContext = loadStoredServiceContext(nameId, databaseUserName)
    if (!serviceContext) {
      AuthenticationService.setOfflineAuthState(usernameLower, false)
      onFailure(ZollAPIService.createMockErrorResponse(401, t('login.loginForm.defaultAuthError')))
      return
    }

    const { serviceList, userId, serviceId, serviceName } = serviceContext
    const userEmail = readStoredEmail(nameId, databaseUserName)
    const authState = AuthenticationService.setOfflineAuthState(usernameLower, true)

    restoreSessionToStore(dispatch, {
      username: usernameLower,
      userId,
      email: userEmail,
      serviceId,
      serviceName,
      serviceList,
      authState,
      databaseUserName,
    })

    setIsLoading(false)

    completeSessionRestore(dispatch, {
      username: usernameLower,
      nameId,
      databaseUserName,
      history,
      successRoute,
    })
  } catch (error) {
    LoggerService.errorObj(`Unexpected error during offline session restore for user: ${usernameLower}`, error)
    setIsLoading(false)
    AuthenticationService.setOfflineAuthState(usernameLower, false)
    onFailure(ZollAPIService.createMockErrorResponse(401, t('login.loginForm.defaultAuthError')))
  }
}
